import {
  PushDeliveryStatus,
  PushFailureReason,
  PushNotificationType,
} from "../../../../../common/enums";

const UNKNOWN_DEVICE = "Непознато устройство";

// Order matters: iPhone/iPad user agents also say "like Mac OS X", and Android ones say "Linux".
const DEVICE_PATTERNS: [RegExp, string][] = [
  [/iPhone|iPod/, "iPhone"],
  [/iPad/, "iPad"],
  [/Android/, "Android"],
  [/Windows/, "Windows"],
  [/Macintosh|Mac OS X/, "Mac"],
  [/Linux|X11/, "Linux"],
];

// Order matters: Edge, Opera and Samsung Internet also say "Chrome", and every Chromium
// browser also says "Safari". Chrome on iPhone says "CriOS", Firefox on iPhone "FxiOS".
const BROWSER_PATTERNS: [RegExp, string][] = [
  [/Edg(e|A|iOS)?\//, "Edge"],
  [/OPR\//, "Opera"],
  [/SamsungBrowser\//, "Samsung Internet"],
  [/Chrome\/|CriOS\//, "Chrome"],
  [/Firefox\/|FxiOS\//, "Firefox"],
  [/Version\/[\d.]+.*Safari\//, "Safari"],
];

const findLabel = (patterns: [RegExp, string][], userAgent: string): string | null =>
  patterns.find(([pattern]) => pattern.test(userAgent))?.[1] ?? null;

/** "<device> · <browser>" for a subscription's user agent, e.g. "iPhone · Safari". */
export const getPushDeviceLabel = (userAgent?: string | null): string => {
  const ua = userAgent?.trim() ?? "";
  if (!ua) return UNKNOWN_DEVICE;

  const device = findLabel(DEVICE_PATTERNS, ua);
  const browser = findLabel(BROWSER_PATTERNS, ua);

  if (device && browser) return `${device} · ${browser}`;
  return device ?? browser ?? UNKNOWN_DEVICE;
};

const DELIVERY_STATUS_LABELS: Record<PushDeliveryStatus, string> = {
  [PushDeliveryStatus.Sent]: "Изпратено",
  [PushDeliveryStatus.Delivered]: "Получено",
  [PushDeliveryStatus.Opened]: "Отворено",
  [PushDeliveryStatus.Failed]: "Неуспешно",
};

/** Where one device's copy got to; a device that unsubscribed says why it failed. */
export const getPushDeliveryStatusLabel = (
  status: PushDeliveryStatus,
  failureReason?: PushFailureReason | null,
): string => {
  if (
    status === PushDeliveryStatus.Failed &&
    failureReason === PushFailureReason.SubscriptionGone
  ) {
    return "Неуспешно: устройството вече не е абонирано (изключени известия или премахната икона от началния екран)";
  }
  return DELIVERY_STATUS_LABELS[status] ?? "";
};

const NOTIFICATION_TYPE_LABELS: Record<PushNotificationType, string> = {
  [PushNotificationType.AppointmentReminder]: "Напомняне",
  [PushNotificationType.NewReservation]: "Нова резервация",
  [PushNotificationType.WaitingListSpotOpened]: "Освободено място",
  [PushNotificationType.NewInvoice]: "Нова фактура",
  [PushNotificationType.Test]: "Тестово известие",
};

export const getPushNotificationTypeLabel = (type: PushNotificationType): string =>
  NOTIFICATION_TYPE_LABELS[type] ?? "Известие";
